import React, { useState, useEffect } from "react";
import { Joyride, EVENTS, STATUS } from "react-joyride";
import { posthog } from "../../config/posthog";
import { parseCurrentUrl } from "../../utils/urlHelper";

const OnboardingTour = () => {
  const [run, setRun] = useState(false);

  useEffect(() => {
    // Check if the user has seen the tour before or opened a facility URL directly
    const hasSeenTour = localStorage.getItem("heatwatch_tour_completed");
    const { facilityId } = parseCurrentUrl();

    if (facilityId && !hasSeenTour) {
      // Mark tour as completed so direct facility link visitors are not interrupted
      localStorage.setItem("heatwatch_tour_completed", "true");
      return;
    }

    if (!hasSeenTour && !facilityId) {
      // Small delay to ensure all components are mounted
      const timer = setTimeout(() => {
        setRun(true);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  const steps = [
    {
      target: ".map-legend",
      content: (
        <span>
          Welcome to the <strong>garment cluster map of India</strong>! This color-coded legend helps you understand the <strong>temperature distribution</strong> across different regions.
        </span>
      ),
      skipBeacon: true,
      placement: "left",
    },
    {
      target: ".onboarding-time-controls-target",
      content: (
        <span>
          Want to see <strong>how things change over time</strong>? Use these controls to <strong>explore different years and specific seasons</strong>.
        </span>
      ),
      skipBeacon: true,
      placement: "top",
    },
    {
      target: ".header-filter-btn",
      content: (
        <span>
          Looking for something specific? Click here to <strong>filter the map by state or organisation</strong>.
        </span>
      ),
      skipBeacon: true,
      placement: "bottom",
    },
    {
      target: ".suggestion-item:nth-child(2)",
      content: (
        <span>
          Need to <strong>find a facility quickly</strong>? You can <strong>select from suggested locations</strong> like this one, or <strong>type to search directly</strong>.
        </span>
      ),
      skipBeacon: true,
      placement: "left",
    },
    {
      target: ".search-type-select",
      content: (
        <span>
          Did you know you can <strong>search by OS ID</strong>? Tap here to <strong>switch between searching by facility name or its unique ID</strong>.
        </span>
      ),
      skipBeacon: true,
      placement: "left",
    },
    {
      target: "#onboarding-methodology-tab",
      content: (
        <span>
          Curious about <strong>how we get our data</strong>? Check out the <strong>Methodology tab</strong> to learn more about our process.
        </span>
      ),
      skipBeacon: true,
      placement: "bottom",
    },
    {
      target: "body",
      content: (
        <span>
          You're <strong>all set</strong>! We hope you <strong>enjoy the dashboard</strong>. Happy exploring!
        </span>
      ),
      skipBeacon: true,
      placement: "center",
    },
  ];

  const handleJoyrideEvent = (data) => {
    const { type, status } = data;

    // Track tour start
    if (type === EVENTS.TOUR_START) {
      posthog.capture("tour_started");
    }

    // TOUR_END fires when the tour finishes or is skipped
    if (type === EVENTS.TOUR_END && [STATUS.FINISHED, STATUS.SKIPPED].includes(status)) {
      setRun(false);
      localStorage.setItem("heatwatch_tour_completed", "true");
      posthog.capture("tour_completed", { status: status });
    }
  };

  return (
    <Joyride
      steps={steps}
      run={run}
      continuous={true}
      showSkipButton={true}
      showProgress={true}
      onEvent={handleJoyrideEvent}
      spotlightPadding={4}
      styles={{
        options: {
          primaryColor: "#2f2f2f",
          textColor: "#374151",
          backgroundColor: "#ffffff",
          arrowColor: "#ffffff",
          zIndex: 10000,
        },
        tooltip: {
          borderRadius: 16,
          border: "none",
          padding: 24,
          fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
        },
        tooltipContent: {
          padding: 0,
        },
        tooltipContainer: {
          textAlign: "left",
          lineHeight: "1.5",
          fontSize: "15px",
        },
        buttonNext: {
          backgroundColor: "#2f2f2f",
          color: "#ffffff",
          borderRadius: "12px",
          padding: "10px 16px",
          fontWeight: 600,
          fontSize: "14px",
        },
        buttonBack: {
          marginRight: 14,
          color: "#6b7280",
          fontWeight: 500,
          fontSize: "14px",
        },
        buttonSkip: {
          color: "#9ca3af",
          fontWeight: 500,
          fontSize: "14px",
        },
        buttonClose: {
          display: "none",
        },
        overlay: {
          zIndex: 9998,
        },
        spotlight: {
          zIndex: 9999,
          borderRadius: "20px",
        },
      }}
      floaterProps={{
        styles: {
          options: {
            zIndex: 10010,
          },
          floater: {
            zIndex: 10010,
          },
          floaterWrapper: {
            zIndex: 10010,
          },
        },
      }}
      locale={{
        last: "Finish",
        skip: "Skip",
      }}
    />
  );
};

export default OnboardingTour;
